// app/components/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl/ManufacturerContent.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CallMadeIcon from "@mui/icons-material/CallMade";
import FactoryIcon from "@mui/icons-material/Factory";
import GrassIcon from "@mui/icons-material/Grass";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import MuiLink from "@mui/material/Link";
import { useTranslations, useLocale } from "next-intl";

const BRAND = "#3d7237";
const BRAND_TINT = "#eef5ec";

// SEO content section: รับ 3 คีย์เวิร์ดเป้าหมายของหน้าบริการ
// (Thailand Tapioca Starch Manufacturer / Non-GMO Cassava Starch /
//  Industrial Tapioca Starch Manufacturing)
export default function ManufacturerContent() {
  const t = useTranslations("OurServices.Manufacturer");
  const tp = useTranslations("ManufacturingProcess");
  const locale = useLocale();
  const processHref =
    locale === "zh"
      ? "/zh/native-tapioca-starch-manufacturing-process"
      : "/native-tapioca-starch-manufacturing-process";
  // หน้า products เป็นเจ้าของคีย์เวิร์ด "Non-GMO Cassava Starch" — หน้านี้ส่ง
  // link ด้วย anchor text ตรงคำแทนที่จะแย่ง rank กันเอง
  const productsHref =
    locale === "zh"
      ? "/zh/thailand-premium-tapioca-starch-products"
      : "/thailand-premium-tapioca-starch-products";

  const industrialItems = [
    t("industrial.item1"),
    t("industrial.item2"),
    t("industrial.item3"),
    t("industrial.item4"),
    t("industrial.item5"),
  ];

  type Section = {
    Icon: typeof FactoryIcon;
    title: string;
    paragraphs: string[];
    link?: { href: string; label: string };
  };

  const sections: Section[] = [
    { Icon: FactoryIcon, title: t("manufacturer.title"), paragraphs: [t("manufacturer.p1")] },
    {
      Icon: GrassIcon,
      title: t("nongmo.title"),
      paragraphs: [t("nongmo.p1"), t("nongmo.p2")],
      link: { href: productsHref, label: t("nongmo.link") },
    },
    { Icon: PrecisionManufacturingIcon, title: t("industrial.title"), paragraphs: [t("industrial.p1")] },
  ];

  return (
    <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: "#fff" }}>
      <Container maxWidth="lg">
        <Typography
          component="h2"
          variant="h4"
          sx={{ fontWeight: 700, color: BRAND, mb: 1 }}
          data-aos="fade-up"
        >
          {t("title")}
        </Typography>
        <Box sx={{ width: 64, height: 3, bgcolor: BRAND, borderRadius: 2, mb: 2.5 }} data-aos="fade-up" />
        <Typography
          variant="body1"
          sx={{ mb: 5, color: "#444", maxWidth: 760, lineHeight: 1.75 }}
          data-aos="fade-up"
        >
          {t("intro")}
        </Typography>

        {sections.map(({ Icon, title, paragraphs, link }, i) => {
          const isIndustrial = i === 2;
          return (
            <Box
              key={title}
              data-aos="fade-up"
              sx={{
                mb: 3,
                border: "1px solid #e5e7eb",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: { xs: 2.5, md: 3 },
                  py: 2,
                  bgcolor: BRAND_TINT,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: BRAND,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 24 }} />
                </Box>
                <Typography component="h3" variant="h6" sx={{ fontWeight: 700, color: BRAND }}>
                  {title}
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2.5, md: 3 }, py: 3 }}>
                {paragraphs.map((p, pi) => (
                  <Typography
                    key={p}
                    variant="body1"
                    sx={{ color: "#444", mb: pi === paragraphs.length - 1 && !isIndustrial ? 0 : 2 }}
                  >
                    {p}
                  </Typography>
                ))}

                {link && (
                  <MuiLink
                    href={link.href}
                    sx={{ color: BRAND, fontWeight: 600, textDecorationColor: BRAND }}
                  >
                    {link.label}
                  </MuiLink>
                )}

                {isIndustrial && (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        columnGap: 2,
                        rowGap: 1,
                        mb: 2,
                      }}
                    >
                      {industrialItems.map((item) => (
                        <Box key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <CheckCircleOutlinedIcon
                            sx={{ fontSize: 18, color: BRAND, mt: "3px", flexShrink: 0 }}
                          />
                          <Typography variant="body2" sx={{ color: "#444" }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="body1" sx={{ color: "#444" }}>
                      {t("industrial.p2")}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          );
        })}

        <Box
          data-aos="fade-up"
          sx={{
            bgcolor: BRAND_TINT,
            borderRadius: 3,
            px: { xs: 2.5, md: 4 },
            py: { xs: 3, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2.5,
          }}
        >
          <Typography variant="body1" sx={{ color: "#2f5a2b", fontWeight: 500 }}>
            {t("closing")}
          </Typography>

          <Button
            color="primary"
            variant="contained"
            endIcon={<CallMadeIcon />}
            size="large"
            href={processHref}
            sx={{
              flexShrink: 0,
              width: { xs: "100%", md: "auto" },
              whiteSpace: { xs: "normal", md: "nowrap" },
            }}
          >
            {tp("Hero.title")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
